import { z } from "zod";

/**
 * ISO-8601 UTC timestamps everywhere on the wire (matching the
 * physical-data design doc).
 */
export const IsoDateTime = z.string().datetime({ offset: true });
export type IsoDateTime = z.infer<typeof IsoDateTime>;

/**
 * Stable ID type. We don't enforce a UUID shape — IDs are always namespaced
 * (`user_...`, `tenant_...`, etc.) per the data model.
 */
export const Id = z.string().min(1).max(128);

/**
 * Standard error envelope returned by every API handler when the response is
 * not 2xx. Mirrors `backend/src/shared/response.ts`.
 */
export const ErrorCode = z.enum([
  "Unauthorized",
  "Forbidden",
  "NotFound",
  "Validation",
  "Conflict",
  "Internal",
  "BadRequest",
  "RateLimited",
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const ErrorEnvelope = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    details: z.unknown().optional(),
    correlationId: z.string().optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelope>;

/**
 * Generic page envelope for list endpoints. The `cursor` is opaque (base64 of
 * the DynamoDB LastEvaluatedKey, see `backend/src/shared/pagination.ts`).
 */
export function Page<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().optional(),
  });
}

/**
 * Mirror of {@link Page} as a discrete type-level helper for callers that need
 * to type a generic page without redefining the inner item.
 */
export type Page<T> = {
  items: T[];
  nextCursor?: string;
};

export const PageQuery = z.object({
  limit: z.coerce.number().int().positive().max(200).default(50),
  cursor: z.string().optional(),
});
export type PageQuery = z.infer<typeof PageQuery>;
